import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ClientSidebar from '../../components/ClientSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { contactService } from '../../services/api';
import { toast } from 'sonner';
import { Phone, Mail, User, Send, Clock, CheckCircle, Headphones, MessageSquare, Building2, Menu } from 'lucide-react';

const HelpContact = () => {
  const { user } = useAuth();
  const [advisor, setAdvisor] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: ''
  });

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [advisorData, messagesData] = await Promise.all([
        user ? contactService.getClientAdvisor(user.id) : contactService.getAdvisor(),
        user ? contactService.getClientMessages(user.id) : []
      ]);
      setAdvisor(advisorData);
      setMessages(messagesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSending(true);
    try {
      await contactService.sendMessage({
        clientId: user.id,
        clientName: `${user.firstName} ${user.lastName}`,
        clientEmail: user.email,
        subject: formData.subject,
        message: formData.message
      });

      toast.success('Message envoyé avec succès');
      setFormData({ subject: '', message: '' });
      loadData();
    } catch (error) {
      toast.error("Impossible d'envoyer le message");
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'replied':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Répondu</span>;
      case 'read':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">Lu</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">En attente</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <ClientSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
        <div className="flex-1 bg-gray-50 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <ClientSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      
      <div className="flex-1 bg-gray-50 min-h-screen md:ml-0">
        {/* Mobile Header with Hamburger - Purple bar without logo */}
        <div className="md:hidden bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 text-white p-4 flex items-center justify-between">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-bold">Aide et contact</h1>
          <div className="w-10"></div>
        </div>

        {/* Desktop Header Banner */}
        <div className="hidden md:block bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 text-white">
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-bold mb-1">Aide et contact</h1>
                <p className="text-purple-200 text-sm">Votre conseiller dédié est à votre écoute</p>
              </div>
              <div className="hidden lg:flex items-center space-x-2 bg-white/10 rounded-lg px-4 py-2">
                <Headphones className="h-5 w-5" />
                <span className="text-sm">Du lundi au vendredi, 9h-18h</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
          
          {/* Top Row - Advisor Info + Contact Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
            
            {/* Advisor Card */}
            <Card className="bg-white border-0 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 md:px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Headphones className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base md:text-lg font-bold text-white">Votre conseiller</h2>
                    <p className="text-purple-200 text-xs md:text-sm truncate">À votre écoute du lundi au vendredi</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 md:p-6 space-y-3 md:space-y-4">
                {/* Advisor Name */}
                <div className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 bg-gray-50 rounded-xl">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Nom du conseiller</p>
                    <p className="font-semibold text-gray-900 text-base md:text-lg truncate">
                      {advisor?.firstName} {advisor?.lastName}
                    </p>
                  </div>
                </div>

                {/* Phone Direct */}
                {advisor?.phoneDirecte && (
                  <div className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 bg-gray-50 rounded-xl">
                    <div className="w-9 h-9 md:w-10 md:h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Téléphone direct</p>
                      <a href={`tel:${advisor.phoneDirecte}`} className="font-semibold text-gray-900 text-base md:text-lg hover:text-purple-600 transition-colors">
                        {advisor.phoneDirecte}
                      </a>
                    </div>
                  </div>
                )}

                {/* Phone Fix */}
                {advisor?.phoneFix && (
                <div className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 bg-gray-50 rounded-xl">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Téléphone fixe</p>
                    <a href={`tel:${advisor.phoneFix}`} className="font-semibold text-gray-900 text-base md:text-lg hover:text-purple-600 transition-colors">
                      {advisor.phoneFix}
                    </a>
                  </div>
                </div>
                )}

                {/* Email */}
                <div className="flex items-center space-x-3 md:space-x-4 p-3 md:p-4 bg-gray-50 rounded-xl">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Email</p>
                    <a href={`mailto:${advisor?.email}`} className="font-semibold text-gray-900 text-sm md:text-base hover:text-purple-600 transition-colors break-all">
                      {advisor?.email || 'contact@younited-credit.fr'}
                    </a>
                  </div>
                </div>

                {/* Hours Info */}
                <div className="p-3 md:p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <div className="flex items-center space-x-2 text-purple-800">
                    <Clock className="h-4 w-4 flex-shrink-0" />
                    <span className="font-medium text-sm">Horaires d'ouverture</span>
                  </div>
                  <p className="text-purple-700 text-sm mt-1">Lundi au Vendredi : 9h00 - 18h00</p>
                </div>
              </div>
            </Card>

            {/* Contact Form Card */}
            <Card className="bg-white border-0 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 md:px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="h-5 w-5 md:h-6 md:w-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base md:text-lg font-bold text-white">Envoyer un message</h2>
                    <p className="text-blue-200 text-xs md:text-sm">Nous vous répondrons sous 24h</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-5">
                <div>
                  <Label htmlFor="subject" className="text-sm font-semibold text-gray-700 mb-2 block">
                    Sujet de votre demande
                  </Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Ex: Question sur mon dossier de prêt"
                    className="h-11 md:h-12 border-gray-200 focus:border-purple-500 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-semibold text-gray-700 mb-2 block">
                    Votre message
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Décrivez votre demande en détail..."
                    rows={5}
                    className="border-gray-200 focus:border-purple-500 focus:ring-purple-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full h-11 md:h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-all"
                >
                  {sending ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Envoi en cours...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Send className="h-5 w-5 mr-2" />
                      Envoyer le message
                    </div>
                  )}
                </Button>
              </form>
            </Card>
          </div>

          {/* Messages History */}
          {messages.length > 0 && (
            <Card className="bg-white border-0 shadow-sm overflow-hidden">
              <div className="bg-gray-800 px-4 md:px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="h-4 w-4 md:h-5 md:w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base md:text-lg font-bold text-white">Historique des messages</h2>
                    <p className="text-gray-400 text-xs md:text-sm">{messages.length} message{messages.length > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
              
              <div className="divide-y divide-gray-100">
                {messages.map((msg) => (
                  <div key={msg.id} className="p-4 md:p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-base md:text-lg truncate">{msg.subject}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <Clock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-xs md:text-sm text-gray-500">
                            {new Date(msg.createdAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusBadge(msg.status)}
                      </div>
                    </div>
                    
                    <p className="text-gray-600 text-sm md:text-base bg-gray-50 rounded-lg p-3 md:p-4 mb-4">{msg.message}</p>

                    {msg.adminReply && (
                      <div className="bg-green-50 rounded-xl p-3 md:p-4 border-l-4 border-green-500">
                        <div className="flex items-center space-x-2 mb-2">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-xs md:text-sm font-semibold text-green-700">Réponse de votre conseiller</span>
                        </div>
                        <p className="text-green-800 text-sm md:text-base">{msg.adminReply}</p>
                        {msg.repliedAt && (
                          <p className="text-xs text-green-600 mt-2">
                            Répondu le {new Date(msg.repliedAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Empty State for Messages */}
          {messages.length === 0 && (
            <Card className="bg-white border-0 shadow-sm p-8 md:p-12 text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-7 w-7 md:h-8 md:w-8 text-gray-400" />
              </div>
              <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Aucun message</h3>
              <p className="text-gray-500 text-sm md:text-base">Vous n'avez pas encore envoyé de message à votre conseiller.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpContact;
